import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Player from '../components/Player';
import ChannelGrid from '../components/ChannelGrid';
import { useChannelContext } from '../context/ChannelContext';
import { categoryLabels } from '../data/channels';

const Home = () => {
  const { 
    channels,
    currentChannel, 
    playChannel, 
    currentFilter, 
    setCurrentFilter,
    getWorldCupChannels 
  } = useChannelContext();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const hasAutoPlayed = useRef(false);
  const refreshCount = useRef(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force re-play on page refresh
  useEffect(() => {
    // Check if this is a page refresh (performance.navigation)
    const isRefresh = performance?.navigation?.type === 1 || 
                      performance?.getEntriesByType?.('navigation')?.[0]?.type === 'reload';
    
    // Also check if there's a channel but it's not playing
    const hasChannelButNotPlaying = currentChannel && !document.querySelector('video')?.currentTime;
    
    if (isRefresh || hasChannelButNotPlaying) {
      console.log('Page refresh detected, re-playing channel...');
      if (currentChannel) {
        // Force re-play the current channel
        playChannel(currentChannel.id);
        hasAutoPlayed.current = true;
      }
    }
  }, []); // Run once on mount

  // Auto-play first channel on load
  useEffect(() => {
    // Only run if channels are loaded and auto-play hasn't been triggered yet
    if (channels.length === 0 || hasAutoPlayed.current) return;
    
    // If no channel is currently playing
    if (!currentChannel) {
      console.log('Auto-playing first channel...');
      
      // Try to play a World Cup channel first
      const wcChannels = getWorldCupChannels();
      if (wcChannels.length > 0) {
        playChannel(wcChannels[0].id);
        hasAutoPlayed.current = true;
      } else if (channels.length > 0) {
        playChannel(channels[0].id);
        hasAutoPlayed.current = true;
      }
    } else {
      // If a channel is already playing, mark as auto-played
      hasAutoPlayed.current = true;
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, currentChannel]);

  // Also try to auto-play when a new channel is added via admin
  useEffect(() => {
    // If channels change and no channel is playing, try to auto-play
    if (channels.length > 0 && !currentChannel && !hasAutoPlayed.current) {
      const wcChannels = getWorldCupChannels();
      if (wcChannels.length > 0) {
        playChannel(wcChannels[0].id);
        hasAutoPlayed.current = true;
      } else {
        playChannel(channels[0].id);
        hasAutoPlayed.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  const handlePlayChannel = (id) => {
    playChannel(id);
    hasAutoPlayed.current = true;
    // On mobile, scroll to player after selecting
    if (isMobile) {
      const playerEl = document.querySelector('.player-section');
      if (playerEl) {
        playerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleFilterChange = (key) => {
    setCurrentFilter(key);
  };

  // Mobile filter tabs data
  const mobileTabs = [
    { key: 'all', label: '📡 সব' },
    { key: 'worldcup', label: '🏆 WC' },
    { key: 'sports', label: '⚽ Sports' },
  ];

  return (
    <div style={{
      paddingTop: '56px',
      display: 'flex',
      minHeight: '100vh',
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      <Sidebar onSelectChannel={handlePlayChannel} />

      <main style={{
        marginLeft: isMobile ? '0' : '200px',
        flex: 1,
        padding: isMobile ? '10px' : '24px',
        width: isMobile ? '100%' : 'auto'
      }}>
        <div className="player-section" style={{
          background: 'var(--surface)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
          marginBottom: '16px'
        }}>
          <Player 
            key={currentChannel?.id || 'player'} // Force re-render on channel change
            channel={currentChannel} 
          />
        </div>

        {/* Mobile Filter Tabs */}
        {isMobile && (
          <div style={{
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            padding: '0 4px 12px',
            marginBottom: '4px',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {mobileTabs.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                style={{
                  background: currentFilter === key ? 'var(--accent)' : 'var(--card)',
                  border: `1px solid ${currentFilter === key ? 'var(--accent)' : 'var(--border)'}`,
                  color: currentFilter === key ? '#fff' : 'var(--muted)',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: '0.2s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minHeight: '36px',
                  touchAction: 'manipulation'
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: isMobile ? '20px' : '22px',
            fontWeight: 600
          }}>
            {categoryLabels[currentFilter] || 'সব চ্যানেল'}
            {currentFilter === 'worldcup' && (
              <span style={{
                fontSize: '12px',
                marginLeft: '8px',
                color: 'var(--worldcup-glow)',
                background: 'rgba(245,200,66,0.15)',
                padding: '2px 10px',
                borderRadius: '12px'
              }}>
                🏆 2026
              </span>
            )}
          </div>
        </div>

        <ChannelGrid onPlayChannel={handlePlayChannel} />
      </main>
    </div>
  );
};

export default Home;